import { IkeToken } from '@ikegps/core-api';
import type { CollectionModel } from '@ikegps/core-shared';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m } from '../../mithril';
import type { TopologicalEditorController } from '../../page/topological.editor.page';
import { Services } from '../../services';
import { classNames } from '../../util/classnames';
import { CSS, Style } from './topological.editor.collection.list.style';

addStyles(Style);

interface ITopologicalEditorCollectionListOptions {
	onItemClick: (id: string) => void;
	selectedRevision?: string | null;
	collections: CollectionModel[];
	collectionsEditController: TopologicalEditorController;
}

export const TopologicalEditorCollectionList = {
	view(options: ITopologicalEditorCollectionListOptions) {
		const collections = options.collections.filter((collection): collection is CollectionModel => collection != null);
		const parentUuids = new Set(collections.filter((c) => c.variantForUuid == null).map((c) => c.uuid));

		// Defense in depth: a department without the collection-variants token should never
		// render the variant grouping, even if a collection's `variantForUuid` link still exists
		// (e.g. left over from before the token was revoked, or moved in from another department).
		const showVariants = Services.Token.has(IkeToken.FEATURE_COLLECTION_VARIANTS);

		// A variant nests under its parent pole when the parent is in view. When a variant layer is
		// selected the parents are filtered out, so those variants have no parent to nest under — render
		// them as their own top-level rows rather than dropping them (they would otherwise vanish).
		const nestedVariants = new Map<string, CollectionModel[]>();
		const topLevel: CollectionModel[] = [];
		for (const collection of collections) {
			const parentUuid = collection.variantForUuid;
			if (showVariants && parentUuid != null && parentUuids.has(parentUuid)) {
				const variants = nestedVariants.get(parentUuid) ?? [];
				variants.push(collection);
				nestedVariants.set(parentUuid, variants);
			} else {
				topLevel.push(collection);
			}
		}

		return m(
			'div',
			{ className: CSS.container },
			topLevel.flatMap((pole) => [
				// Top-level rows — base poles, and variants shown because their layer is selected — are not
				// indented; they align with the pole rows. Only a variant nested under its visible parent
				// gets the child indent.
				TopologicalEditorCollectionList.viewItem(options, pole, false),
				...(nestedVariants.get(pole.uuid) ?? []).map((variant) =>
					TopologicalEditorCollectionList.viewItem(options, variant, true)
				),
			])
		);
	},

	viewItem(options: ITopologicalEditorCollectionListOptions, pole: CollectionModel, nested: boolean) {
		const hasChanges = options.collectionsEditController.collectionHasChanges(pole.revision);
		// Every row shows its collection title — including variants, which carry their parent pole's title.
		// (A variant selected via the layer selector must read as the pole it belongs to, not its type name,
		// matching PhotoMeasure.) UFO-1269.
		const label = pole.title ? pole.title : Translate(T.COLLECTION_MISSING_TITLE);

		return m(
			'div',
			{
				className: classNames(CSS.item, {
					// The child indent marks a variant nested under its visible parent, not a variant row per se —
					// a variant shown on its own (its layer is selected) aligns with the pole rows.
					[CSS.variant]: nested,
					[CSS.selected]: options.selectedRevision === pole.revision,
					[CSS.noLocation]: !pole.location,
				}),
				role: 'button',
				tabindex: '0',
				onclick: () => options.onItemClick(pole.revision),
			},
			[
				m('div', { className: classNames({ [CSS.unsaved]: hasChanges }) }, [
					label,
					pole.isMissingTrueSize() ? m('span', { className: CSS.redDot }) : null,
				]),
				m('div', { className: CSS.info }, hasChanges ? `(${Translate(T.TOPO_UNSAVED_COLLECTION)})` : ''),
			]
		);
	},
};
